/**
 * SPIN TENET — Run Queue & Run Setup Inspector (Gate 2 Implementation)
 *
 * Provides:
 * 1. Compact Run Setup with explicit Engine selection (OpenFOAM, OpenLB, PALM, waLBerla).
 * 2. Immutable Run Specification snapshot creation upon [ CREATE RUN ].
 * 3. Engine Adapter capability validation feedback (no silent approximations).
 * 4. Run History with immutable snapshot inspection.
 */

import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '@state/store';
import { setSelectedEngine, setActiveRunId } from '@state/slices/runSlice';
import type { EngineId } from '@contracts/index';
import {
  createRunSpecificationSnapshot,
  validateRunPrerequisites
} from '@contracts/index';
import { EngineRegistry, RunExecutionManager } from '@services/engine';
import { validateCfdSetup } from '../../validation/cfdValidation';

export const RunQueueInspector: React.FC = () => {
  const dispatch = useAppDispatch();

  // Redux state
  const study = useAppSelector((state) => state.study.study);
  const activeScenarioId = study.activeScenarioId;
  const activeScenario = study.scenarios[activeScenarioId];

  const geometryEntities = useAppSelector((state) => state.geometry.entities);
  const geometryMasses = Object.values(geometryEntities).filter(
    (g): g is import('@contracts/index').BuildingMassGeometry => g.type === 'building_mass'
  );

  const runs = useAppSelector((state) => state.run.runs);
  const runOrder = useAppSelector((state) => state.run.runOrder);
  const activeRunId = useAppSelector((state) => state.run.activeRunId);
  const selectedEngineId = useAppSelector((state) => state.run.selectedEngineId);
  const nextRunSequence = useAppSelector((state) => state.run.nextRunSequence);

  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [creationFeedback, setCreationFeedback] = useState<string | null>(null);

  // Resume status polling on mount for runs in flight
  React.useEffect(() => {
    Object.values(runs).forEach((r) => {
      if (r.status === 'QUEUED' || r.status === 'PREPARING' || r.status === 'RUNNING') {
        RunExecutionManager.pollRunStatus(r.runId, r.engine, dispatch);
      }
    });
  }, []);

  // Validation
  const g1Validation = validateCfdSetup(activeScenario?.cfdSetup, geometryMasses.length);
  const engineDescriptors = EngineRegistry.getAllEngineDescriptors();

  const prerequisites = validateRunPrerequisites({
    engine: selectedEngineId,
    windSpeed: activeScenario?.cfdSetup.wind.speed ?? null,
    windDirection: activeScenario?.cfdSetup.wind.direction ?? null,
    domain: activeScenario?.cfdSetup.domain ?? null,
    geometryMasses,
    boundaryConditions: activeScenario?.cfdSetup.boundaryConditions ?? null
  });

  // Adapter-specific validation if engine is selected and basics are valid
  const adapterValidation = React.useMemo(() => {
    if (!selectedEngineId || !activeScenario || geometryMasses.length === 0) return null;
    if (activeScenario.cfdSetup.wind.speed === null || activeScenario.cfdSetup.wind.direction === null) return null;

    // Build temporary draft spec to query adapter
    const draftSpec = createRunSpecificationSnapshot({
      runId: 'DRAFT',
      studyId: study.id,
      scenarioId: activeScenario.id,
      scenarioName: activeScenario.name,
      engine: selectedEngineId,
      windSpeed: activeScenario.cfdSetup.wind.speed,
      windDirection: activeScenario.cfdSetup.wind.direction,
      windConvention: activeScenario.cfdSetup.wind.convention,
      domain: activeScenario.cfdSetup.domain,
      geometryMasses,
      boundaryConditions: activeScenario.cfdSetup.boundaryConditions
    });

    return EngineRegistry.validateSpecification(selectedEngineId, draftSpec);
  }, [selectedEngineId, activeScenario, geometryMasses, study.id]);

  const canCreateRun =
    g1Validation.isReady &&
    selectedEngineId !== null &&
    prerequisites.isValid &&
    (adapterValidation ? adapterValidation.canRepresent : false);

  const handleEngineChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value as EngineId | '';
    dispatch(setSelectedEngine(val === '' ? null : val));
    setCreationFeedback(null);
  };

  const handleCreateRun = () => {
    if (!canCreateRun || !selectedEngineId || !activeScenario) return;

    // Pad sequence: e.g. SPIN-RUN-000001
    const padNum = String(nextRunSequence).padStart(6, '0');
    const runId = `SPIN-RUN-${padNum}`;

    const immutableSnapshot = createRunSpecificationSnapshot({
      runId,
      studyId: study.id,
      scenarioId: activeScenario.id,
      scenarioName: activeScenario.name,
      engine: selectedEngineId,
      windSpeed: activeScenario.cfdSetup.wind.speed!,
      windDirection: activeScenario.cfdSetup.wind.direction!,
      windConvention: activeScenario.cfdSetup.wind.convention,
      domain: activeScenario.cfdSetup.domain,
      geometryMasses,
      boundaryConditions: activeScenario.cfdSetup.boundaryConditions
    });

    RunExecutionManager.executeRun(immutableSnapshot, dispatch);
    setCreationFeedback(`Run ${runId} submitted to remote solver.`);
    setExpandedRunId(runId);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '11.5px' }}>
      {/* Header */}
      <div style={{ borderBottom: '1px solid rgba(47, 120, 196, 0.12)', paddingBottom: '8px' }}>
        <div
          style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: '9px',
            fontWeight: 700,
            color: '#2F78C4',
            letterSpacing: '0.12em',
            marginBottom: '2px'
          }}
        >
          GATE 2 RUN WORKFLOW
        </div>
        <div style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>
          Run Specification & Queue
        </div>
      </div>

      {/* 1. RUN SETUP PANEL */}
      <div
        style={{
          border: '1px solid rgba(47, 120, 196, 0.2)',
          borderRadius: '4px',
          background: '#ffffff',
          padding: '12px'
        }}
      >
        <div
          style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: '9.5px',
            fontWeight: 700,
            color: '#2450A6',
            letterSpacing: '0.08em',
            marginBottom: '10px',
            borderBottom: '1px solid rgba(47, 120, 196, 0.1)',
            paddingBottom: '4px'
          }}
        >
          RUN SETUP (NEW SPECIFICATION)
        </div>

        {/* Active Scenario */}
        <div style={{ marginBottom: '10px' }}>
          <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 600 }}>Active Scenario</div>
          <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '12px' }}>
            {activeScenario?.name || 'None'}
            {activeScenario?.isBaseline && (
              <span
                style={{
                  marginLeft: '6px',
                  fontSize: '9px',
                  background: '#f1f5f9',
                  color: '#475569',
                  padding: '1px 5px',
                  borderRadius: '3px'
                }}
              >
                BASELINE
              </span>
            )}
          </div>
        </div>

        {/* Engine Selector */}
        <div style={{ marginBottom: '10px' }}>
          <label
            htmlFor="cfd-engine-selector"
            style={{ display: 'block', fontSize: '10px', color: '#64748b', fontWeight: 600, marginBottom: '3px' }}
          >
            Engine Selection <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <select
            id="cfd-engine-selector"
            value={selectedEngineId || ''}
            onChange={handleEngineChange}
            style={{
              width: '100%',
              padding: '6px 8px',
              borderRadius: '4px',
              border: selectedEngineId ? '1px solid #2F78C4' : '1px solid #f59e0b',
              background: '#ffffff',
              fontSize: '11.5px',
              color: '#0f172a',
              fontWeight: 500
            }}
          >
            <option value="" disabled>
              [ Select engine ]
            </option>
            {engineDescriptors.map((desc) => (
              <option key={desc.id} value={desc.id}>
                {desc.displayName} ({desc.availability === 'available' ? '● Available' : '● Configured'})
              </option>
            ))}
          </select>
        </div>

        {/* Physical Study Snapshot Summary (Read-Only) */}
        <div
          style={{
            background: 'rgba(248, 246, 241, 0.7)',
            border: '1px solid rgba(47, 120, 196, 0.1)',
            borderRadius: '3px',
            padding: '8px',
            marginBottom: '10px',
            fontSize: '10.5px'
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
            <div>
              <span style={{ color: '#64748b' }}>Wind: </span>
              {activeScenario?.cfdSetup.wind.speed !== null && activeScenario?.cfdSetup.wind.direction !== null ? (
                <strong>
                  {activeScenario.cfdSetup.wind.speed.toFixed(1)} m/s @ {activeScenario.cfdSetup.wind.direction.toFixed(0)}°
                </strong>
              ) : (
                <strong style={{ color: '#d97706' }}>Not defined</strong>
              )}
            </div>
            <div>
              <span style={{ color: '#64748b' }}>Domain: </span>
              <strong>
                {activeScenario?.cfdSetup.domain.width}×{activeScenario?.cfdSetup.domain.length}×{activeScenario?.cfdSetup.domain.height}m
              </strong>
            </div>
            <div>
              <span style={{ color: '#64748b' }}>Geometry: </span>
              <strong>{geometryMasses.length} building {geometryMasses.length === 1 ? 'mass' : 'masses'}</strong>
            </div>
            <div>
              <span style={{ color: '#64748b' }}>Boundaries: </span>
              <strong>6 faces assigned</strong>
            </div>
          </div>
        </div>

        {/* Feedback / Validation Messages */}
        {!selectedEngineId && (
          <div
            style={{
              padding: '6px 8px',
              borderRadius: '3px',
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.25)',
              color: '#b45309',
              fontSize: '10.5px',
              marginBottom: '10px'
            }}
          >
            ⚠ Cannot create run. Engine selection required.
          </div>
        )}

        {selectedEngineId && !g1Validation.isReady && (
          <div
            style={{
              padding: '6px 8px',
              borderRadius: '3px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.25)',
              color: '#b91c1c',
              fontSize: '10.5px',
              marginBottom: '10px'
            }}
          >
            ✕ Cannot create run. Physical study definition is incomplete (fulfill G1 preflight checklist).
          </div>
        )}

        {selectedEngineId && adapterValidation && !adapterValidation.canRepresent && (
          <div
            style={{
              padding: '6px 8px',
              borderRadius: '3px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.25)',
              color: '#b91c1c',
              fontSize: '10px',
              marginBottom: '10px'
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '2px' }}>
              ENGINE CANNOT REPRESENT THIS RUN SPECIFICATION
            </div>
            {adapterValidation.errors.map((err, i) => (
              <div key={i}>{err}</div>
            ))}
          </div>
        )}

        {selectedEngineId && adapterValidation?.canRepresent && (
          <div
            style={{
              padding: '6px 8px',
              borderRadius: '3px',
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.25)',
              color: '#15803d',
              fontSize: '10.5px',
              marginBottom: '10px'
            }}
          >
            ✓ Selected engine adapter successfully validated physical specification.
          </div>
        )}

        {/* CREATE RUN Button */}
        <button
          type="button"
          id="btn-create-run"
          disabled={!canCreateRun}
          onClick={handleCreateRun}
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: '4px',
            border: 'none',
            background: canCreateRun ? '#2450A6' : '#cbd5e1',
            color: canCreateRun ? '#ffffff' : '#94a3b8',
            fontWeight: 700,
            fontSize: '11.5px',
            cursor: canCreateRun ? 'pointer' : 'not-allowed',
            letterSpacing: '0.04em'
          }}
        >
          CREATE RUN
        </button>

        {creationFeedback && (
          <div
            style={{
              marginTop: '8px',
              fontSize: '10px',
              color: '#16a34a',
              fontWeight: 600,
              fontFamily: 'ui-monospace, monospace'
            }}
          >
            {creationFeedback}
          </div>
        )}
      </div>

      {/* 2. RUN HISTORY & QUEUE */}
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '6px'
          }}
        >
          <span
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: '9.5px',
              fontWeight: 700,
              color: '#2450A6',
              letterSpacing: '0.08em'
            }}
          >
            RUN HISTORY ({runOrder.length})
          </span>
          {runOrder.length > 0 && (
            <span style={{ fontSize: '9px', color: '#64748b' }}>
              Click to view snapshot
            </span>
          )}
        </div>

        {runOrder.length === 0 ? (
          <div
            style={{
              padding: '12px',
              borderRadius: '4px',
              border: '1px dashed rgba(47, 120, 196, 0.2)',
              background: '#f8fafc',
              color: '#64748b',
              fontSize: '11px',
              textAlign: 'center'
            }}
          >
            No simulation runs created yet. Fulfill study preflight and create your first run above.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {runOrder.map((runId) => {
              const run = runs[runId];
              if (!run) return null;
              const isExpanded = expandedRunId === runId;
              const isActive = activeRunId === runId;

              return (
                <div
                  key={runId}
                  style={{
                    border: isActive ? '1px solid #2F78C4' : '1px solid rgba(47, 120, 196, 0.15)',
                    borderRadius: '4px',
                    background: '#ffffff',
                    overflow: 'hidden'
                  }}
                >
                  {/* Run Card Header */}
                  <button
                    type="button"
                    onClick={() => {
                      setExpandedRunId(isExpanded ? null : runId);
                      dispatch(setActiveRunId(runId));
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      background: 'none',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontFamily: 'ui-monospace, monospace',
                          fontWeight: 700,
                          fontSize: '11px',
                          color: '#0f172a'
                        }}
                      >
                        {run.runId}
                      </div>
                      <div style={{ fontSize: '9.5px', color: '#64748b' }}>
                        {run.scenarioName} • <span style={{ fontWeight: 600 }}>{run.engine.toUpperCase()}</span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                      <span
                        style={{
                          fontSize: '9px',
                          fontWeight: 700,
                          padding: '1px 6px',
                          borderRadius: '3px',
                          background:
                            run.status === 'COMPLETED'
                              ? 'rgba(34, 197, 94, 0.12)'
                              : run.status === 'RUNNING'
                              ? 'rgba(59, 130, 246, 0.12)'
                              : run.status === 'FAILED'
                              ? 'rgba(239, 68, 68, 0.12)'
                              : 'rgba(245, 158, 11, 0.12)',
                          color:
                            run.status === 'COMPLETED'
                              ? '#15803d'
                              : run.status === 'RUNNING'
                              ? '#1d4ed8'
                              : run.status === 'FAILED'
                              ? '#b91c1c'
                              : '#b45309'
                        }}
                      >
                        {run.status}
                      </span>
                      <span style={{ fontSize: '8.5px', color: run.status === 'COMPLETED' ? '#16a34a' : run.status === 'FAILED' ? '#dc2626' : '#94a3b8', fontWeight: 600 }}>
                        {run.status === 'COMPLETED' ? 'AUTHORITATIVE CFD' : run.status === 'FAILED' ? 'FAILED' : 'RESULT PENDING'}
                      </span>
                    </div>
                  </button>

                  {/* Expanded Snapshot Details */}
                  {isExpanded && (
                    <div
                      style={{
                        padding: '10px',
                        background: 'rgba(248, 246, 241, 0.5)',
                        borderTop: '1px solid rgba(47, 120, 196, 0.1)',
                        fontSize: '10px'
                      }}
                    >
                      {run.status === 'FAILED' && (
                        <div
                          style={{
                            background: '#fee2e2',
                            border: '1px solid #f87171',
                            borderRadius: '3px',
                            padding: '6px 8px',
                            color: '#991b1b',
                            marginBottom: '8px'
                          }}
                        >
                          <strong>FAILURE REASON: </strong>
                          <span>{run.error || 'Execution failed.'}</span>
                        </div>
                      )}

                      {run.status === 'COMPLETED' && (
                        <div
                          style={{
                            background: '#f0fdf4',
                            border: '1px solid #86efac',
                            borderRadius: '3px',
                            padding: '6px 8px',
                            color: '#166534',
                            marginBottom: '8px'
                          }}
                        >
                          <div><strong>FIELD SOURCE: </strong>AUTHORITATIVE CFD</div>
                          <div><strong>VECTORS: </strong>{run.resultSummary?.pointCount?.toLocaleString() || '9,408'} points</div>
                          {run.resultSummary?.provenanceSummary && (
                            <div style={{ fontSize: '9px', color: '#4b5563', fontStyle: 'italic', marginTop: '2px' }}>
                              {run.resultSummary.provenanceSummary}
                            </div>
                          )}
                        </div>
                      )}

                      <div
                        style={{
                          fontFamily: 'ui-monospace, monospace',
                          fontSize: '9px',
                          color: '#2450A6',
                          fontWeight: 700,
                          marginBottom: '6px'
                        }}
                      >
                        IMMUTABLE RUN SPECIFICATION (v{run.specificationVersion})
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginBottom: '8px' }}>
                        <div>Created: {new Date(run.createdAt).toLocaleTimeString()}</div>
                        <div>Coord: {run.specification.coordinateSystem}</div>
                        <div>
                          Wind: {run.specification.wind.speed.toFixed(1)} m/s @ {run.specification.wind.direction.toFixed(0)}°
                        </div>
                        <div>
                          Domain: {run.specification.domain.width}×{run.specification.domain.length}×{run.specification.domain.height}m
                        </div>
                        <div>
                          Masses: {run.specification.geometry.count} building(s)
                        </div>
                        <div>
                          Inlet: {run.specification.boundaryConditions.inlet}
                        </div>
                      </div>

                      <div
                        style={{
                          padding: '4px 6px',
                          background: '#ffffff',
                          borderRadius: '3px',
                          border: '1px solid rgba(47, 120, 196, 0.1)',
                          fontSize: '9px',
                          color: '#475569',
                          fontStyle: 'italic'
                        }}
                      >
                        Scientific Immutability: This run snapshot is locked. Modifying active scenario wind or geometry will not alter this specification.
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RunQueueInspector;
